import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bot, ArrowRight, ArrowLeft, Users, ShieldCheck, AlertTriangle, Loader2, BarChart3 } from "lucide-react";
import { PlanComparePanel } from "@/components/PlanComparePanel";
import { HelmPlanFinder } from "@/components/case/HelmPlanFinder";
import { isPlanShopping } from "@/lib/planIntent";

const STATE = {
  verified: { label: "Verified", cls: "bg-[#DCFCE7] text-[#166534]", icon: ShieldCheck },
  general: { label: "General guidance", cls: "bg-[#DBEAFE] text-[#1E40AF]", icon: Bot },
  needs_household: { label: "Needs a household", cls: "bg-[#FEF3C7] text-[#92400E]", icon: AlertTriangle },
  unverified: { label: "Not verified", cls: "bg-[#FEE2E2] text-[#991B1B]", icon: AlertTriangle },
};

export default function HelmAsk() {
  const [mode, setMode] = useState("finder");
  const [seed, setSeed] = useState(null);
  const toFinder = (text) => { setSeed(text); setMode("finder"); };
  return (
    <div className={`p-6 sm:p-10 mx-auto ${mode === "finder" ? "max-w-6xl" : "max-w-3xl"}`}>
      <div className="mb-6 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#0B192C] flex items-center justify-center shrink-0"><Bot className="w-6 h-6 text-[#D4AF37]" /></div>
        <div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="ask-title">Ask Nauticus</h1>
          <p className="text-muted-foreground mt-1.5">Two ways to ask — general agent questions, or plan-specific answers inside a household.</p>
        </div>
      </div>

      {mode === "choose" && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="ask-choices">
          <button onClick={() => setMode("finder")} className="sm:col-span-2 text-left rounded-xl border-2 border-[#0B192C] bg-[#0B192C] p-5 text-white hover:border-[#D4AF37] transition-colors" data-testid="ask-choice-finder">
            <Users className="w-6 h-6 text-[#D4AF37]" />
            <h2 className="font-bold mt-3">Plan finder — top 3 for a senior</h2>
            <p className="text-sm text-white/75 mt-1">Type or dictate ZIP, doctors, medications, conditions, Medicaid and priorities. Get up to three explained 2027 choices side by side with doctor, drug and Summary of Benefits checks. Save to a household when ready.</p>
          </button>
          <button onClick={() => setMode("general")} className="text-left rounded-xl border-2 border-[#E5E5E0] bg-white p-5 hover:border-[#D4AF37] transition-colors" data-testid="ask-choice-general">
            <Bot className="w-6 h-6 text-[#0B192C]" />
            <h2 className="font-bold text-[#0B192C] mt-3">Agent questions</h2>
            <p className="text-sm text-[#6B7280] mt-1">General Medicare process from dated, cited sources. No household needed. Never invents carrier benefits.</p>
          </button>
          <button onClick={() => setMode("compare")} className="text-left rounded-xl border-2 border-[#E5E5E0] bg-white p-5 hover:border-[#D4AF37] transition-colors" data-testid="ask-choice-compare">
            <BarChart3 className="w-6 h-6 text-[#0B192C]" />
            <h2 className="font-bold text-[#0B192C] mt-3">Compare 2027 plans</h2>
            <p className="text-sm text-[#6B7280] mt-1">Side-by-side 2027 plan facts for a county via the shared deterministic engine. Unknowns stay explicit; no ranking.</p>
          </button>
          <button onClick={() => setMode("household")} className="text-left rounded-xl border-2 border-[#E5E5E0] bg-white p-5 hover:border-[#D4AF37] transition-colors" data-testid="ask-choice-household">
            <Users className="w-6 h-6 text-[#0B192C]" />
            <h2 className="font-bold text-[#0B192C] mt-3">Ask about a household</h2>
            <p className="text-sm text-[#6B7280] mt-1">Plan-specific answers grounded in a household's confirmed coverage, via the permission-checked CoPilot.</p>
          </button>
        </div>
      )}

      {mode !== "choose" && (
        <Button variant="ghost" onClick={() => setMode("choose")} className="mb-4" data-testid="ask-back"><ArrowLeft className="w-4 h-4 mr-1" /> All Ask options</Button>
      )}
      {mode === "general" && <AgentQuestions onPlanWork={toFinder} />}
      {mode === "compare" && (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="ask-compare">
          <PlanComparePanel variant="agent" />
        </div>
      )}
      {mode === "household" && <HouseholdPicker />}
      {mode === "finder" && <HelmPlanFinder seed={seed} householdId={new URLSearchParams(window.location.search).get("household") || undefined} />}
    </div>
  );
}

const PLAN_WORK = /\b(\d{5}|doctor|dr\.?|physician|provider|in[- ]network|medication|medicine|drug|rx|formular|prescription|compare|top\s*(3|three)|best plan|which plan|recommend)\b/i;

function AgentQuestions({ onPlanWork }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const ask = async () => {
    if (!q.trim()) return;
    if (isPlanShopping(q) || PLAN_WORK.test(q)) { onPlanWork(q.trim()); return; }  // same engine consumers use
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/agent/ask/general", { question: q }); setRes(data); }
    catch { setRes({ state: "unverified", answer: "Something went wrong. Try again.", sources: [] }); }
    finally { setBusy(false); }
  };
  const S = res ? (STATE[res.state] || STATE.unverified) : null;
  return (
    <div data-testid="ask-general">
      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
        <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3} data-testid="ask-general-input"
          placeholder="e.g. When is AEP? How does a SEP work? What's the 2026 Part B premium?"
          className="w-full resize-none rounded-md border p-3 text-sm" />
        <div className="flex flex-wrap gap-2 mt-3">
          {["When is AEP?", "How does the MA Open Enrollment Period work?", "What is the 2026 Part B premium?", "Who qualifies for Extra Help?"].map((s) => (
            <button key={s} onClick={() => setQ(s)} className="text-xs font-semibold px-3 py-1.5 rounded-full border-2 border-[#E5E5E0] hover:border-[#D4AF37]" data-testid="ask-general-starter">{s}</button>
          ))}
        </div>
        <Button onClick={ask} disabled={busy || !q.trim()} className="mt-3 h-10 bg-[#0B192C] text-white" data-testid="ask-general-send">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Ask
        </Button>
      </div>

      {res && (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4 mt-4" data-testid="ask-general-answer">
          <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${S.cls}`} data-testid="ask-general-state">
            <S.icon className="w-3.5 h-3.5" />{S.label}
          </span>
          <p className="text-sm text-[#0B192C] mt-3 whitespace-pre-wrap">{res.answer}</p>
          {res.sources?.length > 0 && (
            <div className="mt-3 border-t pt-3" data-testid="ask-general-sources">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1">Sources</p>
              {res.sources.map((s, i) => (
                <p key={i} className="text-xs text-[#374151]">• {s.title} <span className="text-[#9CA3AF]">(effective {s.effective_date})</span></p>
              ))}
            </div>
          )}
          {res.note && <p className="text-[11px] text-[#9CA3AF] mt-3">{res.note}</p>}
        </div>
      )}
      <p className="text-[11px] text-[#9CA3AF] mt-4">A broader “Agent Intelligence” subscription capability may expand this later; today it answers only from dated sources and won’t guess.</p>
    </div>
  );
}

function HouseholdPicker() {
  const navigate = useNavigate();
  const [households, setHouseholds] = useState([]);
  const [hid, setHid] = useState("");
  useEffect(() => { (async () => { const { data } = await api.get("/agent/households"); setHouseholds(data.households || []); })(); }, []);
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="ask-household">
      <label className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Household</label>
      <select value={hid} onChange={(e) => setHid(e.target.value)} className="mt-1 h-11 w-full rounded-md border bg-white px-2 text-sm" data-testid="ask-household-picker">
        <option value="">Select a household…</option>
        {households.map((h) => <option key={h.household_id} value={h.household_id}>{h.name}{h.state ? ` — ${h.state}` : ""}</option>)}
      </select>
      <Button disabled={!hid} onClick={() => navigate(`/agent/households/${hid}?tab=ask`)} className="mt-4 h-11 w-full bg-[#0B192C] text-white" data-testid="ask-open-copilot">
        Open CoPilot <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
      <p className="text-xs text-[#9CA3AF] mt-3">Answers plan-specific questions for one household from its confirmed coverage. Not a general knowledge assistant.</p>
    </div>
  );
}
